// src/pages/localization/LocalizationKeysPage.tsx
// Translation keys manager — the home of the localization module. Namespace
// tree (left), key list with search/status/missing-for-locale filter
// (middle), per-locale editing grid for the selected key (right).
// Replaces the generated CRUD scaffold — see docs/ux/localization-ia.md.
//
// Note: list_translation_keys.ts / list_translations.ts / list_namespaces.ts
// only accept the generic PaginatedRequest (no query_mapping was declared in
// localization.proto for namespace/status/tag/search), so this page fetches
// once and filters client-side rather than pushing filters to the server.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Alert, Badge, Button } from '@react/components';
import { Select } from '@/components/Select';
import { localizationApi } from '@core/api';
import type { Locale, TranslationKey, TranslationKeyStatus, TranslationNamespace } from '@core/types/localization';
import { NamespaceTree } from '@/features/localization/NamespaceTree';
import { TranslationGrid } from '@/features/localization/TranslationGrid';
import { KeyCreateDialog } from '@/features/localization/KeyCreateDialog';
import { errMessage, keyStatusBadgeVariant } from '@/features/localization/helpers';

export const LocalizationKeysPage = observer(() => {
  const { t } = useI18n();

  const [namespace, setNamespace] = useState<TranslationNamespace | null>(null);
  const [keys, setKeys] = useState<TranslationKey[]>([]);
  const [locales, setLocales] = useState<Locale[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | TranslationKeyStatus>('');
  const [missingLocale, setMissingLocale] = useState('');
  const [missingOnlyIds, setMissingOnlyIds] = useState<Set<string> | null>(null);
  const [selectedKey, setSelectedKey] = useState<TranslationKey | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  const load = () => {
    setIsLoading(true);
    setError(null);
    Promise.all([localizationApi.ListTranslationKeys(), localizationApi.ListLocales()])
      .then(([keysRes, localesRes]) => {
        setKeys(keysRes.items?.items ?? []);
        setLocales(localesRes.items?.items ?? []);
      })
      .catch((e) => setError(errMessage(e, t('localization.keys.loadError'))))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyMissingFilter = async () => {
    if (!missingLocale.trim()) {
      setMissingOnlyIds(null);
      return;
    }
    try {
      const res = await localizationApi.GetMissingTranslations({ locale_code: missingLocale.trim() });
      setMissingOnlyIds(new Set((res.items?.items ?? []).map((k) => k.entity?.id ?? '')));
    } catch (e) {
      setError(errMessage(e, t('localization.keys.loadError')));
    }
  };

  // SearchTranslationKeys' generated endpoint only accepts pagination params —
  // localization.proto declares no query_mapping for `query`, so the actual
  // search text cannot reach the server. This still calls the RPC (merging
  // any results in) and relies on the client-side filter above for the
  // search experience the person actually sees. See docs/ux/localization-ia.md.
  const serverSearch = async () => {
    if (!search.trim()) return;
    try {
      const res = await localizationApi.SearchTranslationKeys();
      const found = res.items?.items ?? [];
      setKeys((prev) => {
        const known = new Set(prev.map((k) => k.entity?.id));
        return [...prev, ...found.filter((k) => !known.has(k.entity?.id))];
      });
    } catch (e) {
      setError(errMessage(e, t('localization.keys.loadError')));
    }
  };

  const filtered = useMemo(() => {
    return keys.filter((k) => {
      if (namespace && k.namespace?.id !== namespace.entity?.id) return false;
      if (statusFilter && k.status !== statusFilter) return false;
      if (missingOnlyIds && !missingOnlyIds.has(k.entity?.id ?? '')) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!k.key.toLowerCase().includes(q) && !k.source_text.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [keys, namespace, statusFilter, missingOnlyIds, search]);

  const doExport = async () => {
    setExportBusy(true);
    setError(null);
    try {
      const res = await localizationApi.BulkExportTranslations();
      const bytes = Uint8Array.from(atob(res.data), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: res.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename || 'translations.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errMessage(e, t('localization.keys.exportError')));
    } finally {
      setExportBusy(false);
    }
  };

  const doImport = async (file: File) => {
    setImportBusy(true);
    setError(null);
    try {
      const buf = await file.arrayBuffer();
      const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
      await localizationApi.BulkImportTranslations({
        entity: { type_id: '', type_name: 'translation', id: '', platform_id: '', tenant_id: '', product_id: '' },
        namespace_slug: namespace?.slug ?? '',
        locale_code: missingLocale || 'en',
        format: 'TRANSLATION_IMPORT_FORMAT_JSON',
        data: b64,
        overwrite_existing: false,
        create_missing_keys: true,
      });
      load();
    } catch (e) {
      setError(errMessage(e, t('localization.keys.importError')));
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 p-6 lg:grid-cols-[200px_1fr_380px]">
      <div className="lg:col-span-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('localization.keys.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('localization.keys.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/localization/glossary" className="text-sm text-primary hover:underline">{t('localization.keys.glossaryLink')}</Link>
          <Link to="/localization/settings" className="text-sm text-primary hover:underline">{t('localization.keys.settingsLink')}</Link>
          <label className="cursor-pointer text-sm text-primary hover:underline">
            {importBusy ? t('localization.keys.importing') : t('localization.keys.import')}
            <input
              type="file"
              accept=".json,.csv,.po,.xliff"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) doImport(f);
                e.target.value = '';
              }}
            />
          </label>
          <Button size="sm" variant="secondary" isLoading={exportBusy} onClick={doExport}>{t('localization.keys.export')}</Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} disabled={!namespace}>{t('localization.keys.newKey')}</Button>
        </div>
      </div>

      {error && <div className="lg:col-span-3"><Alert variant="error">{error}</Alert></div>}

      <NamespaceTree selected={namespace} onSelect={setNamespace} />

      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void serverSearch();
            }}
            placeholder={t('localization.keys.searchPlaceholder')}
            className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground"
          />
          <div className="w-40">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TranslationKeyStatus | '')}
              options={[
                { value: '', label: t('localization.keys.allStatuses') },
                { value: 'TRANSLATION_KEY_STATUS_ACTIVE', label: t('localization.keyStatus.active') },
                { value: 'TRANSLATION_KEY_STATUS_DEPRECATED', label: t('localization.keyStatus.deprecated') },
                { value: 'TRANSLATION_KEY_STATUS_DRAFT', label: t('localization.keyStatus.draft') },
              ]}
              aria-label={t('localization.keys.filterStatus')}
            />
          </div>
          <input
            type="text"
            value={missingLocale}
            onChange={(e) => setMissingLocale(e.target.value)}
            onBlur={applyMissingFilter}
            placeholder={t('localization.keys.missingForLocale')}
            className="h-9 w-36 rounded-md border border-dashed border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {isLoading && <div className="h-64 animate-pulse rounded-md bg-muted" aria-busy="true" />}
        {!isLoading && filtered.length === 0 && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('localization.keys.empty')}</p>
          </div>
        )}
        {!isLoading && (
          <div className="max-h-[70vh] space-y-0.5 overflow-y-auto">
            {filtered.map((k) => (
              <button
                key={k.entity?.id}
                type="button"
                onClick={() => setSelectedKey(k)}
                className={`block w-full rounded-md border px-2.5 py-1.5 text-left ${
                  selectedKey?.entity?.id === k.entity?.id ? 'border-primary bg-accent/40' : 'border-transparent hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="truncate font-mono text-sm text-foreground">{k.key}</span>
                  <Badge variant={keyStatusBadgeVariant(k.status)}>
                    {t(`localization.keyStatus.${(k.status ?? 'TRANSLATION_KEY_STATUS_ACTIVE').replace('TRANSLATION_KEY_STATUS_', '').toLowerCase()}`)}
                  </Badge>
                  {k.is_plural && <Badge variant="secondary">{t('localization.keys.plural')}</Badge>}
                </div>
                <p className="truncate text-xs text-muted-foreground">{k.source_text}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-md border border-border p-3">
        {!selectedKey && <p className="text-sm text-muted-foreground">{t('localization.keys.selectKey')}</p>}
        {selectedKey && (
          <TranslationGrid
            translationKey={selectedKey}
            locales={locales}
            onKeyChanged={(updated) => {
              setSelectedKey(updated);
              setKeys((prev) => prev.map((k) => (k.entity?.id === updated.entity?.id ? updated : k)));
            }}
            onKeyDeleted={() => {
              setSelectedKey(null);
              load();
            }}
          />
        )}
      </div>

      <KeyCreateDialog
        open={createOpen}
        namespace={namespace}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        onError={setError}
      />
    </div>
  );
});
